import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Autocomplete,
  Typography,
  Box,
} from '@mui/material';
import { ProgramSubscription, recordDelivery, fetchSubscriptions } from '../../services/pipelineService';

interface DeliveryDialogProps {
  open: boolean;
  onClose: (success: boolean) => void;
}

interface FormData {
  subscriptionId: string;
  leadCount: string;
  deliveryDate: string;
  leadQualityScore: string;
  notes: string;
}

const DeliveryDialog: React.FC<DeliveryDialogProps> = ({ open, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    subscriptionId: '',
    leadCount: '',
    deliveryDate: new Date().toISOString().split('T')[0],
    leadQualityScore: '',
    notes: '',
  });
  const [subscriptions, setSubscriptions] = useState<ProgramSubscription[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<ProgramSubscription | null>(null);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadSubscriptions();
      resetForm();
    }
  }, [open]);

  const loadSubscriptions = async () => {
    try {
      setLoadingSubscriptions(true);
      const data = await fetchSubscriptions({ status: 'active' });
      setSubscriptions(data);
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setError('Failed to load subscriptions. Please try again.');
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const resetForm = () => {
    setFormData({
      subscriptionId: '',
      leadCount: '',
      deliveryDate: new Date().toISOString().split('T')[0],
      leadQualityScore: '',
      notes: '',
    });
    setSelectedSubscription(null);
    setErrors({});
    setError(null);
  };

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.subscriptionId) {
      newErrors.subscriptionId = 'Subscription is required';
    }

    const leadCount = parseInt(formData.leadCount, 10);
    if (!formData.leadCount || isNaN(leadCount) || leadCount <= 0) {
      newErrors.leadCount = 'Lead count must be a positive integer';
    }

    if (!formData.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required';
    }

    if (formData.leadQualityScore) {
      const qualityScore = parseFloat(formData.leadQualityScore);
      if (isNaN(qualityScore) || qualityScore < 0 || qualityScore > 10) {
        newErrors.leadQualityScore = 'Quality score must be between 0 and 10';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const deliveryData = {
        subscriptionId: formData.subscriptionId,
        leadCount: parseInt(formData.leadCount, 10),
        deliveryDate: formData.deliveryDate,
        leadQualityScore: formData.leadQualityScore ? parseFloat(formData.leadQualityScore) : undefined,
        notes: formData.notes.trim() || undefined,
      };

      await recordDelivery(deliveryData);
      onClose(true);
    } catch (err: any) {
      console.error('Error recording delivery:', err);
      setError(
        err.response?.data?.message ||
        'Failed to record delivery. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Record Lead Delivery</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Subscription Selection */}
          <Grid item xs={12}>
            <Autocomplete
              options={subscriptions}
              getOptionLabel={(option) => {
                const clientInfo = option.client_name || option.client_email || 'Unknown Client';
                const programInfo = option.program_display_name || option.program_name || 'Unknown Program';
                return `${clientInfo} - ${programInfo}`;
              }}
              loading={loadingSubscriptions}
              value={selectedSubscription}
              onChange={(_event, newValue) => {
                setSelectedSubscription(newValue);
                setFormData((prev) => ({
                  ...prev,
                  subscriptionId: newValue ? newValue.id.toString() : '',
                }));
                if (errors.subscriptionId) {
                  setErrors((prev) => ({ ...prev, subscriptionId: undefined }));
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option.client_name || option.client_email || 'Unknown Client'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.program_display_name || option.program_name || 'Unknown Program'}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client Subscription"
                  required
                  error={!!errors.subscriptionId}
                  helperText={errors.subscriptionId || 'Select the client subscription for this delivery'}
                  disabled={submitting}
                />
              )}
            />
          </Grid>

          {/* Delivery Details */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Number of Leads"
              type="number"
              value={formData.leadCount}
              onChange={handleChange('leadCount')}
              error={!!errors.leadCount}
              helperText={errors.leadCount}
              disabled={submitting}
              inputProps={{ min: 1, step: 1 }}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Delivery Date"
              type="date"
              value={formData.deliveryDate}
              onChange={handleChange('deliveryDate')}
              error={!!errors.deliveryDate}
              helperText={errors.deliveryDate}
              disabled={submitting}
              required
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Quality Score */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Lead Quality Score (0-10)"
              type="number"
              value={formData.leadQualityScore}
              onChange={handleChange('leadQualityScore')}
              error={!!errors.leadQualityScore}
              helperText={errors.leadQualityScore || 'Optional: Rate the quality of leads delivered (0-10)'}
              disabled={submitting}
              inputProps={{ min: 0, max: 10, step: 0.1 }}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={3}
              disabled={submitting}
              helperText="Optional notes about this delivery (e.g., lead sources, performance observations)"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : null}
        >
          Record Delivery
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliveryDialog;
